import React, { useState } from "react";
import axios from "axios";
import { Spinner, Table, Button, Container } from "react-bootstrap";
import { Line } from "react-chartjs-2"; // Import Line chart from chart.js
import CountryList from "../CountryList"; // Make sure this file exists and is correctly exported
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

// Registering chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const MarketInsights = () => {
  const [loading, setLoading] = useState(false);
  const [countryCode, setCountryCode] = useState("");
  const [trendingKeywords, setTrendingKeywords] = useState([]);
  const [keyword, setKeyword] = useState("");
  const [regionCode, setRegionCode] = useState("");
  const [trendingData, setTrendingData] = useState([]);
  const [loadingTrending, setLoadingTrending] = useState(false);

  const validCountryCodes = ["US", "IN", "GB", "CA", "AU", "BR", "DE", "FR"];

  // Handle trending country-specific search
  const handleTrendingSearch = async () => {
    if (countryCode.trim()) {
      setLoading(true);
      try {
        const url = `https://azlanarshad.pythonanywhere.com/api/google-scraper/trending/?country_code=${countryCode}`;
        const response = await axios.get(url);
        setTrendingKeywords(response.data.trending_keywords || []);
      } catch (error) {
        console.error("Error fetching trending data:", error);
      } finally {
        setLoading(false);
      }
    }
  };

  // Handle trending keywords search
  const handleKeywordTrendingSearch = async () => {
    if (
      keyword.trim() &&
      validCountryCodes.includes(regionCode.toUpperCase())
    ) {
      setLoadingTrending(true);
      try {
        const url = `https://azlanarshad.pythonanywhere.com/api/google-scraper/keyword-trends/?country_code=${regionCode}&keyword=${keyword}`;
        const response = await axios.get(url);
        setTrendingData(response.data.trend_data || []);
      } catch (error) {
        console.error("Error fetching keyword trending data:", error);
      } finally {
        setLoadingTrending(false);
      }
    }
  };

  // Generate chart data for the first table (trending keywords)
  const generateTrendingKeywordsChartData = () => {
    return {
      labels: trendingKeywords.map((trend) => trend.keyword),
      datasets: [
        {
          label: "Search Volume",
          data: trendingKeywords.map((trend) => trend.search_volume),
          borderColor: "#4cbbff", // Stylish blue color for the line
          backgroundColor: "rgba(76, 187, 255, 0.2)", // Stylish blue color for the area under the line
          fill: true,
          tension: 0.4,
        },
      ],
    };
  };

  // Generate chart data for the second table (trending data for the searched keyword)
  const generateTrendingDataChartData = () => {
    return {
      labels: trendingData.map((trend) => trend.date),
      datasets: [
        {
          label: "Volume",
          data: trendingData.map((trend) => trend.volume),
          borderColor: "#ff6347", // Stylish red color for the line
          backgroundColor: "rgba(255, 99, 71, 0.2)", // Stylish red color for the area under the line
          fill: true,
          tension: 0.4,
        },
      ],
    };
  };

  return (
    <Container>
      <div className="d-flex justify-content-start align-items-center mb-3">
        <div>
          <select
            style={{ fontSize: "0.8rem", padding: "4px 10px" }}
            className="form-control"
            onChange={(e) => setCountryCode(e.target.value)}
            value={countryCode}
          >
            <option value="">Select Country</option>
            <option value="us">United States</option>
            <option value="in">India</option>
            <option value="gb">United Kingdom</option>
            <option value="ca">Canada</option>
            <option value="au">Australia</option>
            <option value="br">Brazil</option>
            <option value="de">Germany</option>
            <option value="fr">France</option>
          </select>
        </div>

        <Button
          variant="primary"
          onClick={handleTrendingSearch}
          style={{ fontSize: "0.8rem", padding: "4px 10px" }}
        >
          {loading ? "Searching..." : "Search Trends"}
        </Button>
      </div>

      {loading ? (
        <div className="text-center">
          <Spinner animation="border" />
        </div>
      ) : (
        <>
          <div style={{ marginBottom: "30px" }}>
            <Line
              data={generateTrendingKeywordsChartData()}
              options={{
                responsive: true,
                plugins: {
                  title: {
                    display: true,
                    text: "Search Volume by Keyword",
                    font: { size: 16 },
                  },
                },
                scales: {
                  y: { beginAtZero: true },
                },
              }}
            />
          </div>

          <Table
            striped
            bordered
            hover
            responsive
            className="shadow-sm"
            style={{ fontSize: "0.7rem", padding: "4px 8px" }}
          >
            <thead>
              <tr>
                <th>Keyword</th>
                <th>Search Volume</th>
              </tr>
            </thead>
            <tbody>
              {trendingKeywords.map((trend, index) => (
                <tr key={index}>
                  <td>{trend.keyword}</td>
                  <td>{trend.search_volume}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </>
      )}

      {/* Section for Trending Keywords with Region */}
      <h2 className=" mb-4 mt-5">
        Would you like to search for Trending Keywords?
      </h2>
      <div className="d-flex justify-content-start align-items-center mb-3">
        <input
          type="text"
          className="form-control"
          placeholder="Enter Keyword"
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          style={{ fontSize: "0.8rem", padding: "4px 10px" }}
        />
        <CountryList
          onSelect={(selectedCountry) => setRegionCode(selectedCountry)}
        />
        <Button
          variant="primary"
          onClick={handleKeywordTrendingSearch}
          style={{ fontSize: "0.8rem", padding: "4px 10px" }}
        >
          {loadingTrending ? "Searching..." : "Search"}
        </Button>
      </div>

      {loadingTrending ? (
        <div className="text-center">
          <Spinner animation="border" />
        </div>
      ) : (
        <>
          <div style={{ marginBottom: "30px" }}>
            <Line
              data={generateTrendingDataChartData()}
              options={{
                responsive: true,
                plugins: {
                  title: {
                    display: true,
                    text: "Volume Over Time for Searched Keyword",
                    font: { size: 16 },
                  },
                },
                scales: {
                  y: { beginAtZero: true },
                },
              }}
            />
          </div>

          <Table
            striped
            bordered
            hover
            responsive
            className="shadow-sm"
            style={{ fontSize: "0.7rem", padding: "4px 8px" }}
          >
            <thead>
              <tr>
                <th>Date</th>
                <th>Volume</th>
              </tr>
            </thead>
            <tbody>
              {trendingData.map((trend, index) => (
                <tr key={index}>
                  <td>{trend.date}</td>
                  <td>{trend.volume}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </>
      )}
    </Container>
  );
};

export default MarketInsights;

